// src/App.tsx - Fixed version
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext.tsx';
import { Navbar } from './components/Navbar.tsx';
import { TabContent } from './components/TabContent.tsx';
import Login from './components/Login.tsx';
import TrainerDashboard from './components/TrainerDashboard.tsx';
import { UserProfile } from './components/UserProfile.tsx';
import ResetPassword from './components/ResetPassword.tsx';
import TermsOfService from './components/TermsOfService.tsx'
import PrivacyPolicy from './components/PrivacyPolicy.tsx';
import DataDeletion from './components/DataDeletion.tsx';
import { useState } from 'react';
import { MessagingProvider } from './contexts/MessagingContext.tsx';
import { GlobalMessagingModals } from './components/messaging/GlobalMessagingModals.tsx';

function AppRoutes() {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [viewingUserId, setViewingUserId] = useState<string | null>(null);

  // Convert URL path to activeTab for TabContent
  const getActiveTabFromPath = (pathname: string): string => {
    if (pathname === '/') return 'home';
    if (pathname.startsWith('/feed')) return 'feed';
    if (pathname.startsWith('/trainers')) return 'trainers';
    if (pathname.startsWith('/services')) return 'services';
    if (pathname.startsWith('/search')) return 'search';
    if (pathname.startsWith('/bookings')) return 'bookings';
    if (pathname.startsWith('/profile') && pathname === '/profile') return 'profile';
    if (pathname.startsWith('/trainer-dashboard')) return 'trainer-dashboard';
    if (pathname.startsWith('/admin-panel')) return 'admin-panel';
    return 'home';
  };

  // Handle tab changes by navigating to the appropriate route
  const handleTabChange = (tab: string) => {
    const routeMap: Record<string, string> = {
      'home': '/',
      'feed': '/feed',
      'trainers': '/trainers',
      'services': '/services',
      'search': '/search',
      'bookings': '/bookings',
      'profile': '/profile',
      'trainer-dashboard': '/trainer-dashboard',
      'admin-panel': '/admin-panel'
    };
    
    navigate(routeMap[tab] || '/');
    setViewingUserId(null);
  };

  // Handle viewing a user profile
  const handleViewUser = (userId: string) => {
    setViewingUserId(userId);
    navigate(`/profile/${userId}`);
  };

  const getBackgroundGradient = (pathname: string) => {
    if (pathname === '/' || pathname === '/home') {
      return 'bg-gradient-to-br from-[#F4C07A] via-[#E6A756] to-[#D2824C]';
    } else if (pathname.startsWith('/feed')) {
      return 'bg-gradient-to-br from-[#B8C5A6] via-[#9CAF88] to-[#7A8471]';
    } else if (pathname.startsWith('/trainers')) {
      return 'bg-gradient-to-br from-[#E6A756] via-[#D2824C] to-[#A0673D]';
    } else if (pathname.startsWith('/bookings')) {
      return 'bg-gradient-to-br from-[#F5EFE7] via-[#F8F4EE] to-[#D4C4B0]';
    } else if (pathname.startsWith('/services')) {
      return 'bg-gradient-to-br from-[#9CAF88] via-[#B8C5A6] to-[#E6A756]';
    } else if (pathname.startsWith('/search')) {
      return 'bg-gradient-to-br from-[#F8F4EE] via-[#F5EFE7] to-[#B8C5A6]';
    } else if (pathname.startsWith('/profile')) {
      return 'bg-gradient-to-br from-[#F4C07A] via-[#F8F4EE] to-[#9CAF88]';
    } else if (pathname.startsWith('/trainer-dashboard')) {
      return 'bg-gradient-to-br from-[#D4C4B0] via-[#A0673D] to-[#8B5A3C]';
    } else if (pathname.startsWith('/admin-panel')) {
      return 'bg-gradient-to-br from-[#8B5A3C] via-[#A0673D] to-[#7A8471]';
    }
    return 'bg-gradient-to-br from-[#F4C07A] via-[#E6A756] to-[#D2824C]';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-200 via-blue-300 to-blue-400">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-white"></div>
      </div>
    );
  }

  // Public routes (accessible without authentication)
  if (!isAuthenticated) {
    return (
      <>
        <Routes>
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="*" element={<Login />} />
        </Routes>
        <GlobalMessagingModals onViewUser={handleViewUser} />
      </>
    );
  }

  // Handle viewing a user profile
  if (viewingUserId || location.pathname.match(/^\/profile\/(.+)$/)) {
    const userIdFromPath = location.pathname.match(/^\/profile\/(.+)$/)?.[1];
    const currentUserId = viewingUserId || userIdFromPath;
    
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-[#F8F4EE] via-[#F5EFE7] to-[#E6A756]">
          <Navbar userRole={user?.role} />
          <div className="container mx-auto px-4 py-6">
            <UserProfile 
              userId={currentUserId!} 
              onClose={() => {
                setViewingUserId(null);
                navigate(-1);
              }} 
              onViewUser={handleViewUser}
            />
          </div>
        </div>
        <GlobalMessagingModals onViewUser={handleViewUser} />
      </>
    );
  }

  // Special handling for trainer dashboard (full screen)
  if (location.pathname === '/trainer-dashboard') {
    return (
      <>
        <TrainerDashboard onNavigateBack={() => navigate('/')} />
        <GlobalMessagingModals onViewUser={handleViewUser} />
      </>
    );
  }

  // Main app with TabContent
  return (
    <>
      <div className={`min-h-screen transition-all duration-700 ${getBackgroundGradient(location.pathname)}`}>
        <Navbar userRole={user?.role} />
        <Routes>
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="*" element={
            <TabContent 
              activeTab={getActiveTabFromPath(location.pathname)}
              onTabChange={handleTabChange}
              onViewUser={handleViewUser}
            />
          } />
        </Routes>
      </div>
      <GlobalMessagingModals onViewUser={handleViewUser} />
    </>
  );
}

function AppContent() {
  return (
    <Router>
      <MessagingProvider>
        <AppRoutes />
      </MessagingProvider>
    </Router>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}